/* eslint-disable no-console */
import _get from 'lodash/get';
import Store from 'lightningjs-redux';
import { Lightning } from '@lightningjs/sdk';
import { layoutService, upFrontTokenService } from '@bedrock/fido-sdk';
import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { getPlayerInstance } from '@bedrock-rdk/media-engine';
import { defaultService, video as videoSettings, featureFlipping } from '@bedrock-rdk/config';
import { createRenderers, enhancePlayerConfig } from '../../../player/player.utils';
import { PlaybackController } from '../../../player/playback.controller';
import { setScreenSaverIsLocked } from '../../../../logic/screenSaver/screenSaver.actions';
import { selectNextPlayableSource } from '../../../../logic/player/player.utils';
import { VIDEO_CAN_PLAY_SIGNAL, VIDEO_ENDED_SIGNAL, VIDEO_PLAYING_SIGNAL } from './trailer.constants';
import { sendEvent } from '../../../../logic/analytics/analytics.utils';

export class TrailerPlayer extends Lightning.Component {
  _construct() {
    this._readyToPlay = false;
    this._videoId = null;
    this._playback = new PlaybackController({
      loadEngine: () => getPlayerInstance().then(({ Engine }) => Engine),
      createSurface: () => {
        const { video, container, subtitles } = createRenderers();

        return { element: video, container, subtitles };
      },
      getOptions: ({ videoId }, { subtitles }) =>
        enhancePlayerConfig({
          dispatcher: this.playerDispatcher.bind(this),
          drm: {
            settings: videoSettings.drm,
            token: {
              handler: () =>
                upFrontTokenService
                  .getVideoToken({
                    videoId,
                    service: defaultService,
                    // user: { UID: uid },
                  })
                  .then(value => ({ value })),
            },
          },
          size: {
            width: `${this.finalW}px`,
            height: `${this.finalH}px`,
          },
          mute: !featureFlipping.player_trailer_sound,
          subtitlesContainer: subtitles,
        }),
    });
  }

  _attach() {
    this._readyToPlay = true;

    if (this._videoId) {
      this._loadVideo(this._videoId);
    }
  }

  _detach() {
    this._readyToPlay = false;
    this._destroyPlayer();
    this._videoId = null;
  }

  get enabled() {
    return featureFlipping.player_trailer;
  }

  set asset(value) {
    if (this._videoId !== value) {
      if (this._videoId) {
        this._destroyPlayer();
      }

      if (this._readyToPlay) {
        this._loadVideo(value);
      }

      this._videoId = value;
    }
  }

  async _loadVideo(videoId) {
    if (!videoId || !featureFlipping.player_trailer) {
      return;
    }

    const [layout] = await Promise.all([
      layoutService.fetchByEntity({ entityType: 'video', entityId: videoId, sectionCode: 'main' }),
      this._playback.preload(),
    ]);

    // NOTE: the trailer could have been detached or switched to another asset while its layout was loading
    if (!this._readyToPlay || this._videoId !== videoId) {
      return;
    }

    const sources = _get(layout, 'blocks[0].content.items[0].itemContent.video.assets');
    const index = selectNextPlayableSource({ sources });

    console.log('[Trailer] Loading video');
    if (!sources[index] || !sources[index].reference) {
      return;
    }

    const player = await this._playback.load({ videoId });
    if (player) {
      player.start({
        ...sources[index],
        shouldAutoPlay: false,
      });
    }
  }

  _destroyPlayer() {
    this._playback.release();

    if (Store.dispatch) {
      Store.dispatch(setScreenSaverIsLocked(false));
    }
  }

  play(startTime) {
    const player = this._playback.engine;
    if (!player) {
      return;
    }

    if (Number.isFinite(startTime)) {
      player.seek(startTime);
    }

    player.play();
  }

  pause() {
    const player = this._playback.engine;
    if (!player) {
      return;
    }

    if (Store.dispatch) {
      Store.dispatch(setScreenSaverIsLocked(false));
    }

    player.pause();
  }

  // eslint-disable-next-line no-unused-vars
  playerDispatcher(event, payload) {
    if (event === VIDEO_CAN_PLAY_SIGNAL) {
      this.signal(VIDEO_CAN_PLAY_SIGNAL);
    } else if (event === VIDEO_PLAYING_SIGNAL) {
      if (Store.dispatch) {
        Store.dispatch(setScreenSaverIsLocked(true));
      }
      this.signal(VIDEO_PLAYING_SIGNAL);
    } else if (event === VIDEO_ENDED_SIGNAL) {
      this.signal(VIDEO_ENDED_SIGNAL);
    } else if (event === VIDEO_EVENTS.ERROR) {
      sendEvent({ key: 'trailerError' });
    }
  }
}
